import React, { useEffect, useMemo, useState } from "react";

import { CreditCardIcon as CreditCard } from "@packages/ds";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useIntl } from "react-intl";
import { useTheme } from "styled-components";

import Button, { Link } from "ds/Button";
import Checkbox from "ds/Checkbox";
import * as Sticker from "ds/Sticker";
import getUrl from "Libs/getUrl";
import client from "Libs/platform";
import { organizationPaymentSourceSelector } from "Reducers/organization/paymentSource/paymentSource.selectors";
import { getOrganizationPaymentSource } from "Reducers/organization/paymentSource/thunks/getOrganizationPaymentSource.thunk";
import {
  getOrganizationProfile,
  organizationProfileSelector
} from "Reducers/organization/profile";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./PaymentElements.styles";

import type { OrganizationAddress } from "Api/organizations/address/types";

type PaymentElementsProps = {
  addressData?: OrganizationAddress;
  organizationId: string;
  organizationDescriptionId: string;
  onSave?: () => void;
  onUpdateChange?: (isUpdating: boolean) => void;
  country?: string;
  secret?: string;
  customSubmitLabel?: string;
  messages?: string[];
};

const messagesMap = {
  sepa_info: "payment_elements.messages.sepa_info"
};

const PaymentElements = ({
  addressData,
  organizationId,
  organizationDescriptionId,
  secret,
  onSave,
  onUpdateChange,
  customSubmitLabel,
  messages
}: PaymentElementsProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [hasAcceptedEscalatingCreditCard, setHasAcceptedEscalatingCreditCard] =
    useState(false);
  const [isPaymentDetailsComplete, setIsPaymentDetailsComplete] =
    useState(false);
  const [error, setError] = useState<string | undefined>();
  const elements = useElements();
  const stripe = useStripe();
  const paymentSource = useAppSelector(state =>
    organizationPaymentSourceSelector(state, {
      organizationId: organizationDescriptionId
    })
  );

  const organizationProfile = useAppSelector(state =>
    organizationProfileSelector(state, {
      organizationId: organizationDescriptionId
    })
  );

  useEffect(() => {
    onUpdateChange?.(isUpdating);
  }, [isUpdating, onUpdateChange]);

  const isSubmitButtonDisabled = hasAcceptedEscalatingCreditCard
    ? !hasAcceptedTerms
    : isLoading || !isPaymentDetailsComplete || !hasAcceptedTerms;

  const expirationMonth = paymentSource?.data?.exp_month;
  const expirationYear = paymentSource?.data?.exp_year;

  const handleSubmit = async event => {
    event.preventDefault();
    setIsLoading(true);
    setError(undefined);

    if (hasAcceptedEscalatingCreditCard) {
      try {
        await client.setVerificationMethodAsPaymentMethod(organizationId);
        dispatch(
          getOrganizationPaymentSource({
            organizationId: organizationDescriptionId
          })
        );
      } catch (err) {
        const error: any = err;
        if (error.detail) {
          setError(error.detail || "Something went wrong");
        } else if (error instanceof Error) {
          setError(error.message);
        } else {
          setError("Something went wrong");
        }
      }
      setIsLoading(false);

      return;
    }

    if (!stripe || !elements) {
      return;
    }

    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: "if_required"
      });

      if (result.error) {
        setError(result.error.message);
      } else {
        const { setupIntent } = await stripe.retrieveSetupIntent(secret!);
        await client.addOrganizationPaymentSource(
          organizationId,
          "stripe",
          setupIntent?.payment_method as string,
          ""
        );
        setIsUpdating(false);
        dispatch(
          getOrganizationPaymentSource({
            organizationId: organizationDescriptionId
          })
        );
        onSave?.();
        dispatch(
          getOrganizationProfile({
            organizationId: organizationDescriptionId
          })
        );
      }
    } catch (err) {
      const error: any = err;
      if (error.detail) {
        setError(error.detail || "Something went wrong");
      } else if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("Something went wrong");
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (organizationDescriptionId) {
      dispatch(
        getOrganizationPaymentSource({
          organizationId: organizationDescriptionId,
          shouldIncludeNonChargeable: true
        })
      );
    }
  }, [dispatch, organizationDescriptionId]);

  if (paymentSource?.chargeable && !isUpdating) {
    return (
      <S.Container>
        <S.Description>
          {intl.formatMessage({ id: "payment_elements.saved.description" })}
        </S.Description>
        <S.PaymentMethodCard>
          <S.PaymentMethodHeaderContainer>
            <S.PaymentMethodHeader>
              <CreditCard />
              {paymentSource.type_label}
            </S.PaymentMethodHeader>
            <Button
              variant="link"
              onClick={() => {
                setIsUpdating(true);
              }}
              analyticId="payment_elements.update"
            >
              {intl.formatMessage({ id: "payment_elements.update" })}
            </Button>
          </S.PaymentMethodHeaderContainer>
          {paymentSource.number && (
            <S.PaymentMethodNumber data-pendo-redact-text>
              {paymentSource.number}
            </S.PaymentMethodNumber>
          )}
          {expirationMonth && expirationYear && (
            <S.PaymentMethodNumber data-pendo-redact-text>
              {intl.formatMessage({
                id: "payment_elements.expiration"
              })}
              {":"} {expirationMonth}/{expirationYear}
            </S.PaymentMethodNumber>
          )}
          {(paymentSource.data?.mandate_reference ||
            paymentSource.data?.mandate_url) && (
            <S.PaymentMethodNumber>
              {paymentSource.data.mandate_url ? (
                <Link
                  href={paymentSource.data.mandate_url}
                  analyticId="payment_elements.mandate"
                >
                  {intl.formatMessage({
                    id: "payment_elements.mandate"
                  })}
                  {paymentSource.data?.mandate_reference &&
                    `: ${paymentSource.data?.mandate_reference}`}
                </Link>
              ) : (
                `${intl.formatMessage({
                  id: "payment_elements.mandate"
                })}:
                ${paymentSource.data.mandate_reference}`
              )}
            </S.PaymentMethodNumber>
          )}
        </S.PaymentMethodCard>
        <Checkbox
          forId="terms"
          id="terms"
          checked
          disabled
          label={intl.formatMessage({ id: "payment_elements.checkbox" })}
        />
      </S.Container>
    );
  }

  const sepaRequestQueryParameters = new URLSearchParams({
    category: "billing_question",
    priority: "normal",
    subject: intl.formatMessage({
      id: "payment_elements.messages.sepa_info.subject"
    }),
    isGeneral: "true"
  });

  const sepaRequestUrl = getUrl({
    key: "users.tickets.open",
    props: { username: "-" }
  });

  return (
    <S.Container>
      {!organizationProfile?.billing_contact && (
        <S.AddressDisclaimer>
          {intl.formatMessage({ id: "payment_elements.address_disclaimer" })}
        </S.AddressDisclaimer>
      )}
      <S.Description>
        {intl.formatMessage({ id: "payment_elements.description" })}{" "}
        {messages?.map(message =>
          intl.formatMessage(
            {
              id: messagesMap[message]
            },
            {
              link: text => (
                <Link
                  to={`${sepaRequestUrl}?${sepaRequestQueryParameters.toString()}`}
                >
                  {text}
                </Link>
              )
            }
          )
        )}
      </S.Description>
      {paymentSource?.number && !paymentSource.chargeable && (
        <>
          <Checkbox
            forId="credit-card-escalation"
            id="credit-card-escalation"
            onCheckedChanged={() => {
              setHasAcceptedEscalatingCreditCard(
                !hasAcceptedEscalatingCreditCard
              );
            }}
            checked={hasAcceptedEscalatingCreditCard}
            label={intl.formatMessage({
              id: "payment_elements.escalate_credit_card"
            })}
          />
          {hasAcceptedEscalatingCreditCard && (
            <S.PaymentMethodCard>
              <S.PaymentMethodHeaderContainer>
                <S.PaymentMethodHeader>
                  <CreditCard />
                  {paymentSource.type_label ??
                    intl.formatMessage({
                      id: `payment_elements.${paymentSource.type}`
                    })}
                </S.PaymentMethodHeader>
              </S.PaymentMethodHeaderContainer>
              {paymentSource.number && (
                <S.PaymentMethodNumber>
                  {paymentSource.number}
                </S.PaymentMethodNumber>
              )}
              {expirationMonth && expirationYear && (
                <S.PaymentMethodNumber>
                  {intl.formatMessage({
                    id: "payment_elements.expiration"
                  })}
                  {":"} {expirationMonth}/{expirationYear}
                </S.PaymentMethodNumber>
              )}
            </S.PaymentMethodCard>
          )}
        </>
      )}
      {paymentSource?.number && !paymentSource.chargeable ? (
        !hasAcceptedEscalatingCreditCard && (
          <PaymentElement
            data-pendo-redact-text
            onChange={event => {
              setIsPaymentDetailsComplete(event.complete);
            }}
          />
        )
      ) : (
        <PaymentElement
          data-pendo-redact-text
          options={{
            defaultValues: {
              billingDetails: {
                address: {
                  line1: addressData?.thoroughfare,
                  line2: addressData?.premise,
                  postal_code: addressData?.postal_code,
                  city: addressData?.locality,
                  state: addressData?.administrative_area
                }
              }
            }
          }}
          onChange={event => {
            setIsPaymentDetailsComplete(event.complete);
          }}
        />
      )}
      <Checkbox
        forId="terms"
        id="terms"
        onCheckedChanged={() => {
          setHasAcceptedTerms(!hasAcceptedTerms);
        }}
        checked={hasAcceptedTerms}
        label={intl.formatMessage({ id: "payment_elements.checkbox" })}
      />
      <div>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitButtonDisabled}
          analyticId={customSubmitLabel}
        >
          {customSubmitLabel || intl.formatMessage({ id: "submit" })}
        </Button>
      </div>
      {isLoading && <S.LoadingIcon />}
      {error && (
        <Sticker.Root priority="critical">
          <Sticker.Icon priority="critical" />
          <Sticker.Text>{error}</Sticker.Text>
        </Sticker.Root>
      )}
    </S.Container>
  );
};

const StripeWrapper = (props: PaymentElementsProps) => {
  const theme = useTheme();
  const [secret, setSecret] = useState(undefined);
  const [publicKey, setPublic] = useState(undefined);
  const [messages, setMessages] = useState([]);
  const [isUpdatingStripeInstance, setIsUpdatingStripeInstance] =
    useState(false);
  const isDarkMode = useMemo(() => theme.name === "dark", [theme]);

  useEffect(() => {
    async function loadIntent() {
      const intent = await client.createOrganizationPaymentSourceIntent(
        props.organizationId
      );
      setSecret(intent.client_secret);
      setPublic(intent.public_key);
      setMessages(intent.messages);
      setIsUpdatingStripeInstance(false);
    }
    setIsUpdatingStripeInstance(true);
    loadIntent();
  }, [props.organizationId, props.country]);

  const stripePromise = useMemo(() => {
    if (publicKey) return loadStripe(publicKey);
  }, [publicKey]);

  if (!secret || !publicKey || !stripePromise || isUpdatingStripeInstance) {
    return <S.LoadingIcon />;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: secret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: window
              .getComputedStyle(document.body)
              .getPropertyValue("--color-primary-600"),
            colorText: window
              .getComputedStyle(document.body)
              .getPropertyValue(
                isDarkMode ? "--section-slate-color" : "--ebony-light"
              ),
            colorTextSecondary: window
              .getComputedStyle(document.body)
              .getPropertyValue("--granite"),
            colorTextPlaceholder: window
              .getComputedStyle(document.body)
              .getPropertyValue("--granite"),
            colorBackground: isDarkMode
              ? window
                  .getComputedStyle(document.body)
                  .getPropertyValue("--mode-bg-neutral-weakest")
              : undefined
          }
        }
      }}
    >
      <PaymentElements {...props} secret={secret} messages={messages} />
    </Elements>
  );
};

export default StripeWrapper;
